import axios from 'axios';
import {API_BASE_URL} from './authApi';

export interface TalentSearchFilters {
  keyword?: string;
  category?: TalentCategoryId;
  sort?: TalentSortId;
  sport?: string;
  artForm?: string;
  serviceType?: string;
  skillLevel?: string;
  proficiencyLevel?: string;
  location?: string;
}

export type TalentCategoryId = 'all' | 'athletic' | 'artistic' | 'general';
export type TalentSortId = 'recommended' | 'nameAsc' | 'nameDesc' | 'newest';

export interface TalentSearchUser {
  _id: string;
  email?: string;
  isFollowing: boolean;
  previewImageUrl?: string;
  profile?: {
    name?: string;
    bio?: string;
    profilePicture?: string;
    location?: string;
  };
  athleticServices?: Array<{
    sportName: string;
    skillLevel: string;
    yearsOfExperience?: number;
    certifications?: string[];
    availability?: string;
  }>;
  artisticExperiences?: Array<{
    artForm: string;
    proficiencyLevel: string;
    portfolioLinks?: string[];
    yearsOfPractice?: number;
    serviceRates?: string;
  }>;
  generalServices?: Array<{
    title: string;
    description: string;
    pricing?: string;
    availability?: string;
    contactPreferences?: string;
  }>;
  mediaContent?: Array<{
    type: 'video' | 'image';
    url?: string;
    thumbnailUrl?: string;
  }>;
  highlightReels?: Array<{
    isFeatured: boolean;
  }>;
}

export interface TalentSearchResponse {
  success: boolean;
  users: TalentSearchUser[];
  total: number;
  page: number;
  totalPages: number;
}

export interface TalentRecommendation {
  _id: string;
  profile?: {
    name?: string;
    profilePicture?: string;
  };
}

export interface TalentRecommendationsResponse {
  success: boolean;
  suggestions: TalentRecommendation[];
}

type TalentSearchErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

export class TalentSearchApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'TalentSearchApiError';
    this.status = status;
  }
}

const talentSearchClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as TalentSearchErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

export const talentSearchApi = {
  async getRecommendations(
    token: string,
    limit: number = 6,
    signal?: AbortSignal
  ): Promise<TalentRecommendationsResponse> {
    try {
      const response = await talentSearchClient.get<TalentRecommendationsResponse>('/search/recommendations', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {limit},
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new TalentSearchApiError(
        getErrorMessage(error, 'Failed to load recommendations'),
        axios.isAxiosError(error) ? error.response?.status : undefined
      );
    }
  },

  async searchUsers(
    filters: TalentSearchFilters,
    page: number,
    limit: number,
    token: string,
    signal?: AbortSignal
  ): Promise<TalentSearchResponse> {
    try {
      const response = await talentSearchClient.get<TalentSearchResponse>('/search/users', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          ...filters,
          page,
          limit,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new TalentSearchApiError(
        getErrorMessage(error, 'Failed to load talent search results'),
        axios.isAxiosError(error) ? error.response?.status : undefined
      );
    }
  },
};
